/**
 * @file bin-label-print.ts
 * @description Печать этикеток ячеек, полок и стеллажей (phase19.22 шаг 5) —
 *   чистые функции без React: что выбрано, какое тело запроса и сколько
 *   листов. Проверяются тестом.
 *
 * Контракт — docs/api/wms-labels.md §«Этикетки ячеек». Правила, которые
 * легко сломать правкой диалога:
 *   - стеллаж выбран целиком (все его активные ячейки и только они) — шлём
 *     `rack`, иначе `binIds`: сервер по `rack` возьмёт и ячейки, заведённые
 *     после открытия страницы; `rack` сервер принимает только одной
 *     латинской буквой — иначе тоже `binIds`;
 *   - этикетки полок и стеллажа — по одной на полку/стеллаж выбранных ячеек,
 *     копии — у КАЖДОЙ этикетки (ячейки, полки, стеллажа), лимит 500 с копиями;
 *   - стеллажная этикетка — всегда «Стандарт», ячейки и полки — выбранный
 *     шаблон (по умолчанию «Компакт»), всё на тех же листах A4;
 *   - «начать с N» — только по шаблонам, которые реально идут в печать.
 */

import { ApiError } from "../../../lib/api-client";
import type {
  BinLabelPrintInput,
  BinLabelTemplate,
  LabelCodeMode,
  LabelStartAt,
} from "../../../lib/api-labels";
import type { StorageBinDto } from "../../../lib/api-bins";
import type { LabelSheetLine } from "../../../lib/label-sheets";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Шаблон этикеток ячеек по умолчанию: адрес короткий, «Компакт» хватает. */
export const BIN_LABEL_DEFAULT_TEMPLATE: BinLabelTemplate = "COMPACT";
/** Стеллажная этикетка крупная — её читают издалека. */
export const RACK_LABEL_TEMPLATE: BinLabelTemplate = "STANDARD";
/** Потолок копий каждой этикетки (контракт API). */
export const BIN_LABEL_MAX_COPIES = 5;
/** Потолок этикеток за одну печать адресов, с учётом копий (контракт API). */
export const BIN_LABEL_MAX_LABELS = 500;
/** Формат `rack`, который принимает сервер. */
const RACK_PARAM = /^[A-Za-z]$/;

/** Параметры печати из диалога. */
export interface BinLabelOptions {
  copies: number;
  template: BinLabelTemplate;
  includeShelfLabels: boolean;
  includeRackLabel: boolean;
  codeMode: LabelCodeMode;
  startAt: LabelStartAt;
  /** Линии реза — печать на обычной бумаге (настройка рабочего места). */
  cutLines: boolean;
}

/** Что печатаем: отмеченные ячейки или стеллаж целиком. */
export interface BinLabelScope {
  binIds: string[];
  /** Стеллаж, если выбраны ровно все его активные ячейки. */
  rack: string | null;
}

/** Стеллажи справочника: код → активные ячейки (порядок обхода). */
export function groupRacks(bins: StorageBinDto[]): Array<{ rack: string; binIds: string[] }> {
  const byRack = new Map<string, StorageBinDto[]>();
  for (const bin of bins) {
    if (!bin.isActive) continue;
    byRack.set(bin.rack, [...(byRack.get(bin.rack) ?? []), bin]);
  }
  return [...byRack.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "ru"))
    .map(([rack, list]) => ({
      rack,
      binIds: [...list].sort((x, y) => x.sortKey - y.sortKey).map((b) => b.id),
    }));
}

/** Определить охват: стеллаж целиком → `rack`, иначе список ячеек. */
export function selectionScope(selected: StorageBinDto[], all: StorageBinDto[]): BinLabelScope {
  const binIds = selected.map((b) => b.id);
  const racks = new Set(selected.map((b) => b.rack));
  if (racks.size !== 1) return { binIds, rack: null };
  const [rack] = [...racks];
  const rackActive = all.filter((b) => b.isActive && b.rack === rack);
  const whole = rackActive.length === selected.length && rackActive.every((b) => binIds.includes(b.id));
  return { binIds, rack: whole && rack && RACK_PARAM.test(rack) ? rack : null };
}

/** Сколько этикеток полок и стеллажей даст выбор. */
export function levelCounts(selected: StorageBinDto[]): { shelves: number; racks: number } {
  return {
    shelves: new Set(selected.map((b) => `${b.rack}\u0000${b.shelf}`)).size,
    racks: new Set(selected.map((b) => b.rack)).size,
  };
}

/** Строки для сводки листов (`summarizeSheets`). */
export function binLabelLines(selected: StorageBinDto[], options: BinLabelOptions): LabelSheetLine[] {
  const { shelves, racks } = levelCounts(selected);
  const copies = options.copies;
  const lines: LabelSheetLine[] = [{ template: options.template, quantity: selected.length * copies }];
  if (options.includeShelfLabels) lines.push({ template: options.template, quantity: shelves * copies });
  if (options.includeRackLabel) lines.push({ template: RACK_LABEL_TEMPLATE, quantity: racks * copies });
  return lines;
}

/** Тело `POST /admin/wms/labels/print-bins`. */
export function buildBinLabelPrintInput(
  warehouseId: string,
  scope: BinLabelScope,
  options: BinLabelOptions,
): BinLabelPrintInput {
  const used = new Set<BinLabelTemplate>([options.template]);
  if (options.includeRackLabel) used.add(RACK_LABEL_TEMPLATE);
  return {
    warehouseId,
    ...(scope.rack ? { rack: scope.rack } : { binIds: scope.binIds }),
    includeShelfLabels: options.includeShelfLabels,
    includeRackLabel: options.includeRackLabel,
    copies: Math.min(Math.max(1, Math.trunc(options.copies)), BIN_LABEL_MAX_COPIES),
    template: options.template,
    startAt: Object.fromEntries([...used].map((t) => [t, options.startAt[t] ?? 1])),
    codeMode: options.codeMode,
    cutLines: options.cutLines,
  };
}

/** Детали ошибок печати адресов, которые нужны тексту. */
interface BinLabelErrorDetails {
  codes?: string[];
}

/**
 * Тексты ошибок печати адресов — реестром (OCP). Свои, а не общие складские:
 * там «приёмка проведена» и «не больше 5000», здесь ни то, ни другое не так.
 * `retryable` — повтор тем же запросом имеет смысл.
 */
const BIN_LABEL_ERROR_TEXT: Record<string, (d: BinLabelErrorDetails) => { text: string; retryable: boolean }> = {
  label_render_failed: () => ({ text: "Сервис печати сейчас недоступен — повторите через минуту.", retryable: true }),
  rate_limit_exceeded: () => ({ text: "Слишком много печатей подряд — подождите минуту и повторите.", retryable: true }),
  labels_limit_exceeded: () => ({
    text: `Не больше ${BIN_LABEL_MAX_LABELS} этикеток за раз с учётом копий — разбейте печать по стеллажам.`,
    retryable: false,
  }),
  bin_archived: (d) => ({
    text: d.codes?.length
      ? `В архиве: ${d.codes.join(", ")} — обновите страницу и снимите их с выбора.`
      : "Часть ячеек в архиве — обновите страницу и снимите их с выбора.",
    retryable: false,
  }),
  bin_rack_empty: () => ({ text: "На стеллаже нет активных ячеек — печатать нечего.", retryable: false }),
  bin_not_found: () => ({ text: "Часть ячеек не найдена на складе — обновите страницу.", retryable: false }),
  bin_labels_empty: () => ({ text: "Отметьте ячейки или выберите стеллаж.", retryable: false }),
};

/** Ошибка печати ячеек → текст для `Alert` и признак «Повторить». */
export function binLabelErrorText(err: unknown): { text: string; retryable: boolean } {
  if (err instanceof ApiError) {
    const code = err.status === 429 ? "rate_limit_exceeded" : err.code;
    const build = BIN_LABEL_ERROR_TEXT[code];
    if (build) return build((err.details ?? {}) as BinLabelErrorDetails);
  }
  return { text: wmsErrorText(err, "Не удалось сформировать этикетки ячеек"), retryable: false };
}
